import { UserInfoResponse, UserRole } from "common";

/**
 * The membership row for the organization actually being viewed. The organization pages used to
 * read userOrganizations[0], which is whichever organization happens to sort first, so an admin
 * of two organizations saw the first one's name, tabs and data while the URL named the other.
 */
export const organizationMembershipFor = (
  user: UserInfoResponse | undefined,
  organizationId: string
): UserInfoResponse["userOrganizations"][number] | undefined =>
  user?.userOrganizations?.find(o => o.id === organizationId);

/**
 * Whether the user may open the organization pages for this organization at all: a member, or a
 * platform admin, matching the backend. undefined means "not decidable yet" (the user has not
 * loaded): callers must not redirect on it, or the page bounces before the answer exists.
 */
export const canViewOrganization = (
  user: UserInfoResponse | undefined,
  organizationId: string
): boolean | undefined => {
  if (!user) return undefined;
  if (user.userRole === UserRole.Admin) return true;
  return organizationMembershipFor(user, organizationId) !== undefined;
};

/**
 * Whether the user may manage this organization (Users, Run Environments, Invoices, Settings):
 * its org_admin, or a platform admin without a membership, matching the backend's controller
 * check. undefined has the same meaning as above.
 */
export const canManageOrganization = (
  user: UserInfoResponse | undefined,
  organizationId: string
): boolean | undefined => {
  if (!user) return undefined;
  if (user.userRole === UserRole.Admin) return true;
  return (
    organizationMembershipFor(user, organizationId)?.role?.name === "org_admin"
  );
};

/**
 * Whether the organization pages may ask for subscription health. The route sits behind the
 * Walkins Welcome flag and answers 404 without it. The layout and Settings share one query, so
 * both must use this gate: when Settings started the query on its own, the layout showed its
 * loading skeleton, which unmounted Settings, which started the query again on remount (#1070).
 */
export const canCheckSubscriptionHealth = (
  user: UserInfoResponse | undefined
): boolean => user?.featureFlags?.FF_WALKINS_WELCOME === true;

type Membership = UserInfoResponse["userOrganizations"][number];

const lastOrganizationSlot = (user: UserInfoResponse) =>
  `viafoundry.lastOrganization.${user.id}`;

/**
 * Records the organization the user just opened, so the Organization link and /activity can come
 * back to it after a reload. Per user, because a browser can be shared. Storage can be blocked
 * (private mode, cleared site data), and losing the memory is harmless, so a failure is ignored.
 */
export const rememberOrganization = (
  user: UserInfoResponse | undefined,
  organizationId: string
): void => {
  if (!user || !organizationId) return;
  try {
    localStorage.setItem(lastOrganizationSlot(user), organizationId);
  } catch {
    // Nothing to do: the next visit falls back to the first active membership.
  }
};

const storedOrganizationId = (user: UserInfoResponse): string | null => {
  try {
    return localStorage.getItem(lastOrganizationSlot(user));
  } catch {
    return null;
  }
};

/**
 * The organization the side navigation and /activity should open (#1069). userOrganizations[0]
 * is whichever membership sorts first, so a member of two organizations always landed on the same
 * one. In order: the organization selected in this session, the one last visited in this browser,
 * then the first active membership. Only an active membership qualifies, so a stale memory of an
 * organization the user has left or lost is passed over.
 */
export const defaultOrganizationFor = (
  user: UserInfoResponse | undefined,
  selectedOrganizationId?: string
): Membership | undefined => {
  const active = (user?.userOrganizations ?? []).filter(
    o => o.status === "active"
  );
  if (!user || active.length === 0) return undefined;
  for (const candidate of [
    selectedOrganizationId,
    storedOrganizationId(user),
  ]) {
    const match = candidate && active.find(o => o.id === candidate);
    if (match) return match;
  }
  return active[0];
};

/**
 * Whether any membership is active, wherever it sorts (#1134). The access gates used to read
 * userOrganizations[0], so an inactive newest organization locked a member of several
 * organizations out of the whole app even with another membership active.
 */
export const hasActiveMembership = (
  user: UserInfoResponse | undefined
): boolean => (user?.userOrganizations ?? []).some(o => o.status === "active");

/**
 * Whether the user must use role based AWS access. Any membership asserting the policy asserts it
 * for the user, matching the backend's getRequireRoleBasedAwsAuthForUser, which also counts
 * inactive memberships. Reading only the first membership let the form and the backend disagree.
 */
export const requiresRoleBasedAwsAuth = (
  user: UserInfoResponse | undefined
): boolean =>
  (user?.userOrganizations ?? []).some(
    o => o.settings?.requireRoleBasedAwsAuth === true
  );

/**
 * Whether the user may add a folder path to a dataset. The frontend is this setting's only gate,
 * so it fails closed: any membership turning it off turns it off for the user.
 */
export const allowsDownloadFolder = (
  user: UserInfoResponse | undefined
): boolean =>
  (user?.userOrganizations ?? []).every(
    o => o.settings?.allowDownloadFolder !== false
  );
